import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';
import type { Span } from '@sentry/core';
import { ulid } from 'ulid';
import type { AnalyticsCacheStatus } from '../models/analytics-cache-status.model';
import type { SourceConfig } from '../models/chart-config.model';

/** GraphQL operations that trigger async card data over AppSync Events. */
export type AnalyticsDataOperation = 'getCard' | 'getCardData' | 'getPageData';

export const ANALYTICS_DATA_LATENCY_OP = 'analytics.data.latency';

/** Default orphan timeout for pending HTTP → WS spans (ms). */
export const ANALYTICS_DATA_LATENCY_TIMEOUT_MS = 120_000;

/** Client-generated ULID echoed by the backend on matching WebSocket payloads. */
export function createAnalyticsDataRequestId(): string {
	return ulid();
}

export interface AnalyticsDataLatencyRequestStart {
	requestId: string;
	operation: AnalyticsDataOperation;
	cardId?: string;
	pageId?: string;
	viewId?: string;
	accountId?: string;
	dataSourceServices?: string[];
}

export interface AnalyticsDataLatencyWebSocketComplete {
	requestId: string;
	cardId?: string;
	pageId?: string;
	executionId?: string;
	cardType?: string;
	dataSourceServices?: string[];
	/** QE/AQ cache tier reported with the payload. */
	cacheStatus?: AnalyticsCacheStatus;
}

interface PendingSpan {
	span: Span;
	timeoutId: ReturnType<typeof setTimeout>;
}

/** Data-source slugs from a card config for Sentry grouping. */
export function dataSourceServicesFromCardConfig(config: unknown): string[] {
	if (!config || typeof config !== 'object' || !('sources' in config)) {
		return [];
	}
	const sources = (config as { sources?: SourceConfig[] }).sources;
	return extractDataSourceServices(sources);
}

/** Unique data-source slugs from card `sources` for Sentry grouping. */
export function extractDataSourceServices(
	sources: SourceConfig[] | undefined
): string[] {
	if (!sources?.length) {
		return [];
	}
	const slugs = sources
		.map(source => source.dataSource?.trim())
		.filter((slug): slug is string => Boolean(slug));
	return [...new Set(slugs)];
}

function dataSourceAttribute(services: string[] | undefined): string | undefined {
	if (!services?.length) {
		return undefined;
	}
	return services.join(',');
}

function createInactiveSpan(
	options: Parameters<typeof Sentry.startInactiveSpan>[0]
): Span | undefined {
	if (typeof Sentry.startInactiveSpan !== 'function') {
		return undefined;
	}
	return Sentry.startInactiveSpan(options);
}

function applyCompletionAttributes(
	span: Span,
	complete: AnalyticsDataLatencyWebSocketComplete
): void {
	span.setAttribute('graphql.request_id', complete.requestId);
	if (complete.executionId) {
		span.setAttribute('graphql.execution_id', complete.executionId);
	}
	if (complete.cardType) {
		span.setAttribute('graphql.card_type', complete.cardType);
	}
	if (complete.cardId) {
		span.setAttribute('graphql.card_id', complete.cardId);
	}
	if (complete.pageId) {
		span.setAttribute('graphql.page_id', complete.pageId);
	}
	if (complete.cacheStatus) {
		span.setAttribute('cache_status', complete.cacheStatus);
	}
	const dataSources = dataSourceAttribute(complete.dataSourceServices);
	if (dataSources) {
		span.setAttribute('graphql.data_source.service', dataSources);
	}
}

function requestAttributes(
	start: AnalyticsDataLatencyRequestStart
): Record<string, string> {
	const attrs: Record<string, string> = {
		'graphql.operation': start.operation,
		'graphql.request_id': start.requestId,
	};
	if (start.cardId) {
		attrs['graphql.card_id'] = start.cardId;
	}
	if (start.pageId) {
		attrs['graphql.page_id'] = start.pageId;
	}
	if (start.viewId) {
		attrs['graphql.view_id'] = start.viewId;
	}
	if (start.accountId) {
		attrs['graphql.account_id'] = start.accountId;
	}
	const dataSources = dataSourceAttribute(start.dataSourceServices);
	if (dataSources) {
		attrs['graphql.data_source.service'] = dataSources;
	}
	return attrs;
}

/**
 * Tracks end-to-end latency from GraphQL data requests to matching WebSocket payloads.
 *
 * Correlation is **strictly by `requestId`**: the client generates a ULID, sends it on
 * the GraphQL request, and completes the span when a WebSocket event echoes the same id.
 *
 * Query in Sentry Traces Explorer:
 * `span.op:analytics.data.latency` — group by `graphql.data_source.service`
 * or `graphql.operation` for p50/p95.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsDataLatencyService {
	private readonly _pendingByRequestId = new Map<string, PendingSpan>();

	markRequest(start: AnalyticsDataLatencyRequestStart): void {
		const requestId = start.requestId?.trim();
		if (!requestId) {
			return;
		}

		const span = createInactiveSpan({
			name: `graphql.${start.operation} → ws.response [${requestId}]`,
			op: ANALYTICS_DATA_LATENCY_OP,
			attributes: requestAttributes(start),
		});
		if (!span) {
			return;
		}

		const timeoutId = setTimeout(() => {
			this.#timeoutSpan(requestId, span);
		}, ANALYTICS_DATA_LATENCY_TIMEOUT_MS);

		const existing = this._pendingByRequestId.get(requestId);
		if (existing) {
			clearTimeout(existing.timeoutId);
		}

		this._pendingByRequestId.set(requestId, { span, timeoutId });
	}

	completeFromWebSocket(
		complete: AnalyticsDataLatencyWebSocketComplete
	): void {
		this.#complete(complete, 'websocket');
	}

	/**
	 * Card data that came back inline on the GraphQL response instead of over
	 * AppSync, so the span closes on the same request that opened it.
	 */
	completeFromInlineResponse(
		complete: AnalyticsDataLatencyWebSocketComplete
	): void {
		this.#complete(complete, 'inline');
	}

	#complete(
		complete: AnalyticsDataLatencyWebSocketComplete,
		source: 'websocket' | 'inline'
	): void {
		const requestId = complete.requestId?.trim();
		if (!requestId) {
			return;
		}

		const pending = this._pendingByRequestId.get(requestId);
		if (!pending) {
			return;
		}

		clearTimeout(pending.timeoutId);
		this._pendingByRequestId.delete(requestId);
		applyCompletionAttributes(pending.span, complete);
		pending.span.setAttribute('graphql.response.source', source);
		pending.span.end();
	}

	#timeoutSpan(requestId: string, span: Span): void {
		const pending = this._pendingByRequestId.get(requestId);
		if (!pending || pending.span !== span) {
			return;
		}
		this._pendingByRequestId.delete(requestId);
		pending.span.setStatus({ code: 2, message: 'deadline_exceeded' });
		pending.span.end();
	}
}
